import React, { useEffect, useRef, useState } from 'react';
import { MdClose, MdOutlineCalculate, MdShowChart } from 'react-icons/md';
import './index.css';

// Bluebook-style calculator panel: one draggable, corner-resizable popup with a
// Graphing | Scientific toggle. Both Desmos instances mount once and stay
// mounted while the panel is closed, so each keeps its expressions and the
// panel reopens on whichever mode was used last.

type CalculatorMode = 'graphing' | 'scientific';

type Props = {
    open: boolean;
    onClose: () => void;
    // The host page's popup header class, so the bar matches its other popups.
    headerClassName: string;
};

const DEFAULT_WIDTH = 390;
const DEFAULT_HEIGHT = 640;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 420;
const WINDOW_MARGIN = 8;

const scientificEnabled = () => {
    try {
        return Boolean(Desmos.enabledFeatures?.ScientificCalculator);
    }
    catch {
        return false;
    }
};

function DesmosCalculatorPanel({ open, onClose, headerClassName }: Props) {
    const [mode, setMode] = useState<CalculatorMode>('graphing');
    const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
    const [hasScientific] = useState(scientificEnabled);

    const panelRef = useRef<HTMLDivElement>(null);
    const graphingElRef = useRef<HTMLDivElement>(null);
    const scientificElRef = useRef<HTMLDivElement>(null);
    const graphingRef = useRef<Desmos.Calculator | null>(null);
    const scientificRef = useRef<Desmos.BasicCalculator | null>(null);

    useEffect(() => {
        if (graphingElRef.current) {
            graphingRef.current = Desmos.GraphingCalculator(graphingElRef.current, {
                notes: false,
                folders: false,
                images: false,
                links: false
            });
        }
        if (hasScientific && scientificElRef.current) {
            scientificRef.current = Desmos.ScientificCalculator(scientificElRef.current, {
                links: false
            });
        }

        return () => {
            graphingRef.current?.destroy();
            scientificRef.current?.destroy();
            graphingRef.current = null;
            scientificRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!open) return;
        const active = mode === 'scientific' ? scientificRef.current : graphingRef.current;
        active?.resize();
        active?.focusFirstExpression();
    }, [open, mode]);

    useEffect(() => {
        graphingRef.current?.resize();
        scientificRef.current?.resize();
    }, [size]);

    // The largest body size that keeps the whole panel inside the window, from
    // where the panel currently sits. The window wins over MIN_WIDTH/HEIGHT.
    const windowLimits = () => {
        const panel = panelRef.current;
        if (!panel) return { maxWidth: DEFAULT_WIDTH, maxHeight: DEFAULT_HEIGHT };
        const rect = panel.getBoundingClientRect();
        const headerHeight = panel.firstElementChild?.getBoundingClientRect().height ?? 0;
        return {
            maxWidth: Math.max(0, window.innerWidth - Math.max(0, rect.left) - WINDOW_MARGIN),
            maxHeight: Math.max(0, window.innerHeight - Math.max(0, rect.top) - headerHeight - WINDOW_MARGIN)
        };
    };

    // Shrink to fit when the panel opens or the browser window gets smaller.
    useEffect(() => {
        if (!open) return;
        const fitToWindow = () => {
            const { maxWidth, maxHeight } = windowLimits();
            setSize(prev => (prev.width <= maxWidth && prev.height <= maxHeight)
                ? prev
                : { width: Math.min(prev.width, maxWidth), height: Math.min(prev.height, maxHeight) });
        };
        fitToWindow();
        window.addEventListener('resize', fitToWindow);
        return () => window.removeEventListener('resize', fitToWindow);
    }, [open]);

    // Drag by the header. Clicks on the toggle or the close button are not drags.
    const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        if (target.closest('.desmosPanel__toggle, .desmosPanel__close')) return;
        const panel = panelRef.current;
        if (!panel) return;

        event.preventDefault();
        let lastX = event.clientX;
        let lastY = event.clientY;

        const onMove = (moveEvent: PointerEvent) => {
            const { clientX, clientY } = moveEvent;
            if (clientX <= 0 || clientY <= 0 || clientX >= window.innerWidth || clientY >= window.innerHeight) return;
            panel.style.left = `${panel.offsetLeft + clientX - lastX}px`;
            panel.style.top = `${panel.offsetTop + clientY - lastY}px`;
            lastX = clientX;
            lastY = clientY;
        };
        const onUp = () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
    };

    // Resize from the bottom-right handle, clamped to the viewport.
    const startResize = (event: React.PointerEvent<HTMLButtonElement>) => {
        const panel = panelRef.current;
        if (!panel) return;

        event.preventDefault();
        event.stopPropagation();
        const startX = event.clientX;
        const startY = event.clientY;
        const startWidth = size.width;
        const startHeight = size.height;
        const { maxWidth, maxHeight } = windowLimits();

        const onMove = (moveEvent: PointerEvent) => {
            setSize({
                width: Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + moveEvent.clientX - startX)),
                height: Math.min(maxHeight, Math.max(MIN_HEIGHT, startHeight + moveEvent.clientY - startY))
            });
        };
        const onUp = () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
    };

    return (
        <div
            ref={panelRef}
            className={open ? 'desmosPanel' : 'desmosPanel desmosPanel--hidden'}
            aria-hidden={!open}
        >
            <div className={`${headerClassName} desmosPanel__header`} onPointerDown={startDrag}>
                {hasScientific
                    ? <div className='desmosPanel__toggle' role='tablist' aria-label='Calculator type'>
                        <button
                            type='button'
                            role='tab'
                            aria-selected={mode === 'graphing'}
                            className={mode === 'graphing' ? 'desmosPanel__toggleOption--active' : 'desmosPanel__toggleOption'}
                            onClick={() => setMode('graphing')}
                        >
                            <MdShowChart className='desmosPanel__toggleIcon' />
                            <span className='desmosPanel__toggleText'>Graphing</span>
                        </button>
                        <button
                            type='button'
                            role='tab'
                            aria-selected={mode === 'scientific'}
                            className={mode === 'scientific' ? 'desmosPanel__toggleOption--active' : 'desmosPanel__toggleOption'}
                            onClick={() => setMode('scientific')}
                        >
                            <MdOutlineCalculate className='desmosPanel__toggleIcon' />
                            <span className='desmosPanel__toggleText'>Scientific</span>
                        </button>
                    </div>
                    : <h3 className='desmosPanel__title'>Calculator</h3>
                }
                <button type='button' className='desmosPanel__close' aria-label='Close calculator' onClick={onClose}>
                    <MdClose />
                </button>
            </div>
            <div className='desmosPanel__body' style={{ width: size.width, height: size.height }}>
                <div
                    ref={graphingElRef}
                    className={mode === 'graphing' ? 'desmosPanel__calculator' : 'desmosPanel__calculator desmosPanel__calculator--hidden'}
                />
                {hasScientific &&
                    <div
                        ref={scientificElRef}
                        className={mode === 'scientific' ? 'desmosPanel__calculator' : 'desmosPanel__calculator desmosPanel__calculator--hidden'}
                    />
                }
            </div>
            <button
                type='button'
                className='desmosPanel__resize'
                aria-label='Resize calculator'
                onPointerDown={startResize}
            >
                <svg viewBox='0 0 16 16' width='14' height='14' aria-hidden='true'>
                    <path d='M3 3h5M3 3v5M3 3l10 10M13 13H8M13 13V8' stroke='currentColor' strokeWidth='1.6' fill='none' strokeLinecap='round' />
                </svg>
            </button>
        </div>
    );
}

export default DesmosCalculatorPanel;
